import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";

// ---------------------------------------------------------------------------
// Normalize a raw category record from the API into the shape the UI expects
// ---------------------------------------------------------------------------
const normalizeCategory = (item) => {
  if (!item) return null;

  const isActive =
    item.bIsActive !== undefined
      ? Boolean(item.bIsActive && item.bIsActive !== 0 && item.bIsActive !== "0")
      : true;

  // sType from API: "filter" → "Filter", "collection" → "Collection"
  const rawType = (item.sType || "").toLowerCase();
  const type = rawType === "collection" ? "Collection" : "Filter";

  // modeName from API: "mannequin_to_model" → "Mannequin to Model", "model_to_model" → "Model to Model"
  const rawMode = (item.modeName || "").toLowerCase();
  let mode = item.modeName || "";
  if (rawMode === "mannequin_to_model") mode = "Mannequin to Model";
  else if (rawMode === "model_to_model") mode = "Model to Model";

  return {
    ...item,
    id: item.iCategoryId,
    name: item.sName || item.categoryName || "",
    type,
    gender: item.genderName || "",
    mode,
    isActive,
    bIsActive: isActive,
  };
};

const DEBOUNCE_DELAY = 350; // ms
const LIMIT_OPTIONS = [10, 20, 50, 100];

export const useCategoryListing = () => {
  const { token } = useSelector((state) => state.auth || {});

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Server-driven pagination info
  const [serverPage, setServerPage] = useState(1);
  const [serverLimit, setServerLimit] = useState(10);
  const [serverPagination, setServerPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // Filter groups returned by the API (e.g. gender, type, mode)
  const [filterGroups, setFilterGroups] = useState([]);

  // Applied filters: { gender: "Women", type: "filter", mode: "model_to_model" }
  const [appliedFilters, setAppliedFilters] = useState({});

  // Raw search input (bound to input field)
  const [searchInput, setSearchInput] = useState("");
  // Debounced search that triggers the actual API call
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);

  // Debounce timer ref
  const debounceRef = useRef(null);
  // Refs to always hold the latest values — prevents stale closure in fetchCategories
  const appliedFiltersRef = useRef({});
  const debouncedSearchRef = useRef("");
  const serverPageRef = useRef(1);
  const serverLimitRef = useRef(10);

  // ---------------------------------------------------------------------------
  // Debounce search input
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const newSearch = searchInput;
      setDebouncedSearch(newSearch);
      setServerPage(1);
      debouncedSearchRef.current = newSearch;
      serverPageRef.current = 1;
      // Trigger fetch directly so debounced search always fires with latest filters
      fetchCategories(1, newSearch, appliedFiltersRef.current);
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // ---------------------------------------------------------------------------
  // Build query string
  // ---------------------------------------------------------------------------
  const buildQueryString = useCallback((page, search, filters, limit = 10) => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", String(limit));
    if (search && search.trim()) {
      params.set("search", search.trim());
    }
    if (filters && typeof filters === "object") {
      Object.entries(filters).forEach(([key, value]) => {
        if (value && String(value).trim()) {
          params.set(key, String(value).trim());
        }
      });
    }
    return params.toString();
  }, []);

  // ---------------------------------------------------------------------------
  // Fetch categories from API
  // ---------------------------------------------------------------------------
  const fetchCategories = useCallback(
    async (page, search, filters) => {
      if (!token) return;

      try {
        setLoading(true);
        setError(null);

        const qs = buildQueryString(page, search, filters, serverLimitRef.current);
        const url = `${endpoint.API_BASE_URL}/api/categories?${qs}`;

        const response = await apiHandler({
          url,
          method: "GET",
          authToken: { token },
          noCache: true,
        });

        const data = response?.data?.data;

        if (response?.data?.status === "success" && data) {
          setCategories(
            Array.isArray(data.rows)
              ? data.rows.map(normalizeCategory).filter(Boolean)
              : []
          );

          setServerPagination({
            page: data.page ?? page,
            limit: data.limit ?? 10,
            total: data.total ?? 0,
            totalPages: data.totalPages ?? 1,
          });

          if (Array.isArray(data.filters)) {
            setFilterGroups(data.filters);
          }
        } else {
          setCategories([]);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
        setError(err?.message || "Failed to load categories.");
        setCategories([]);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, buildQueryString]
  );

  // Trigger fetch on initial mount, token change, limit change, or server-page change
  useEffect(() => {
    serverPageRef.current = serverPage;
    serverLimitRef.current = serverLimit;
    fetchCategories(serverPage, debouncedSearchRef.current, appliedFiltersRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverPage, serverLimit, token]);

  // ---------------------------------------------------------------------------
  // Navigation handlers
  // ---------------------------------------------------------------------------
  const handlePageChange = useCallback((newPage) => {
    setServerPage(newPage);
  }, []);

  const handleLimitChange = useCallback((newLimit) => {
    const limit = Number(newLimit) || 10;
    serverLimitRef.current = limit;
    serverPageRef.current = 1;
    setServerLimit(limit);
    setServerPage(1);
    // Direct call so limit change always fires with latest filters/search
    fetchCategories(1, debouncedSearchRef.current, appliedFiltersRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCategories]);

  const handleSearchChange = useCallback((value) => {
    const raw = value?.target ? value.target.value : value;
    setSearchInput(raw);
  }, []);

  // Called by FilterOptions when filters are applied
  // filterObj shape: { gender: ["Women"], type: ["filter"], mode: [...] }
  // We flatten single-select arrays to scalar values for query params
  const handleFilterChange = useCallback((filterObj) => {
    const flattened = {};
    if (filterObj && typeof filterObj === "object") {
      Object.entries(filterObj).forEach(([key, val]) => {
        if (Array.isArray(val)) {
          flattened[key] = val.length > 0 ? val[0] : "";
        } else {
          flattened[key] = val;
        }
      });
    }
    // Update ref immediately so the direct fetchCategories call below uses the new value
    appliedFiltersRef.current = flattened;
    serverPageRef.current = 1;
    setAppliedFilters(flattened);
    setServerPage(1);
    // Call fetchCategories directly — don't rely on useEffect object-reference comparison
    fetchCategories(1, debouncedSearchRef.current, flattened);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCategories]);

  // ---------------------------------------------------------------------------
  // Modal handlers
  // ---------------------------------------------------------------------------
  const openAddModal = useCallback(() => {
    setCategoryToEdit(null);
    setIsModalOpen(true);
  }, []);

  const openEditModal = useCallback((cat) => {
    setCategoryToEdit(cat);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setCategoryToEdit(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Map UI display values → API body fields
  // ---------------------------------------------------------------------------
  const buildApiPayload = (savedData) => {
    // type: "filter" → "filter", "collection" → "collection"
    const sType =
      (savedData.type || "").toLowerCase() === "collection" ? "collection" : "filter";

    // mode: "Mannequin to Model" → "mannequin_to_model", "Model to Model" → "model_to_model"
    let ModeName = savedData.mode || "";
    if (savedData.mode === "Mannequin to Model") ModeName = "mannequin_to_model";
    else if (savedData.mode === "Model to Model") ModeName = "model_to_model";

    return {
      sName: (savedData.name || "").trim().replace(/\s+/g, " "),
      sType,
      GenderName: savedData.gender,
      ModeName,
      bIsActive: savedData.isActive ? 1 : 0,
    };
  };

  // Save — calls POST (create) or PUT (update) then refreshes
  const handleSaveCategory = useCallback(
    async (savedData) => {
      const isNew = !savedData.id || String(savedData.id).startsWith("cat-");
      const payload = buildApiPayload(savedData);

      try {
        if (isNew) {
          const response = await apiHandler({
            url: `${endpoint.API_BASE_URL}/api/categories`,
            method: "POST",
            authToken: { token },
            data: payload,
          });

          if (
            (response?.status && response.status >= 400) ||
            response?.data?.status === "error"
          ) {
            throw new Error(
              response?.data?.message || "Failed to create category"
            );
          }

          toast.success("Category added successfully!");
        } else {
          const response = await apiHandler({
            url: `${endpoint.API_BASE_URL}/api/categories/${savedData.id}`,
            method: "PUT",
            authToken: { token },
            data: payload,
          });

          if (
            (response?.status && response.status >= 400) ||
            response?.data?.status === "error"
          ) {
            throw new Error(
              response?.data?.message || "Failed to update category"
            );
          }

          toast.success("Category updated successfully!");
        }

        fetchCategories(serverPageRef.current, debouncedSearchRef.current, appliedFiltersRef.current);
      } catch (err) {
        console.error("Save category error:", err);
        toast.error(err?.message || "Failed to save category.");
        throw err; // re-throw so modal stays open
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, fetchCategories]
  );

  // ---------------------------------------------------------------------------
  // Toggle status — optimistic with rollback
  // ---------------------------------------------------------------------------
  const handleToggleStatus = useCallback(
    async (id) => {
      const target = categories.find((item) => item.id === id);
      if (!target) return;

      const previousState = Boolean(target.isActive);
      const newState = !previousState;

      // Optimistic update
      setCategories((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, isActive: newState, bIsActive: newState }
            : item
        )
      );

      try {
        const response = await apiHandler({
          url: `${endpoint.TOGGLE_CATEGORY}/${id}/toggle`,
          method: "PATCH",
          authToken: { token },
          noCache: true,
        });

        if (
          (response?.status && response.status >= 400) ||
          response?.data?.status === "error" ||
          response?.data?.success === false
        ) {
          throw new Error(response?.data?.message || "Failed to update status");
        }

        toast.success(
          `Category "${target.name}" marked as ${newState ? "active" : "inactive"}`
        );
      } catch (err) {
        console.error("Toggle error:", err);
        toast.error(err?.message || "Failed to update category status.");
        // Rollback
        setCategories((prev) =>
          prev.map((item) =>
            item.id === id
              ? { ...item, isActive: previousState, bIsActive: previousState }
              : item
          )
        );
      }
    },
    [categories, token]
  );

  // ---------------------------------------------------------------------------
  // Delete category — optimistic with rollback
  // ---------------------------------------------------------------------------
  const handleDeleteCategory = useCallback(
    async (id) => {
      const target = categories.find((item) => item.id === id);

      // Optimistic removal
      setCategories((prev) => prev.filter((item) => item.id !== id));

      try {
        const response = await apiHandler({
          url: `${endpoint.API_BASE_URL}/api/categories/${id}`,
          method: "DELETE",
          authToken: { token },
        });

        if (
          (response?.status && response.status >= 400) ||
          response?.data?.status === "error"
        ) {
          throw new Error(response?.data?.message || "Failed to delete category");
        }

        if (target) {
          toast.success(`Deleted category "${target.name}"`);
        }
      } catch (err) {
        console.error("Delete error:", err);
        toast.error(err?.message || "Failed to delete category.");
        fetchCategories(serverPageRef.current, debouncedSearchRef.current, appliedFiltersRef.current); // Re-fetch to rollback
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories, token, fetchCategories]
  );

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------
  return {
    categories,
    loading,
    error,
    // Search
    searchInput,
    onSearchChange: handleSearchChange,
    // Filters (from API)
    filterGroups,
    appliedFilters,
    onFilterChange: handleFilterChange,
    // Server-side pagination
    pagination: {
      page: serverPagination.page,
      limit: serverLimit,
      total: serverPagination.total,
      totalPages: serverPagination.totalPages,
      onPageChange: handlePageChange,
      limitOptions: LIMIT_OPTIONS,
      onLimitChange: handleLimitChange,
    },
    // Modal
    isModalOpen,
    categoryToEdit,
    openAddModal,
    openEditModal,
    closeModal,
    handleSaveCategory,
    // Row actions
    handleToggleStatus,
    handleDeleteCategory,
    refreshCategories: () =>
      fetchCategories(serverPageRef.current, debouncedSearchRef.current, appliedFiltersRef.current),
  };
};
